/**
 * Telegram-style chat preview.
 *
 * Renders the template as it would appear in a Telegram chat — own-message
 * bubble (right-aligned, accent gradient), with timestamp + double-tick.
 * Background mimics the standard pattern wallpaper used by Telegram dark mode.
 *
 * This is intentionally a *visual mock*: we don't reproduce Telegram's actual
 * theme, just give the user a recognisable preview of formatting + emojis +
 * media in a chat context.
 */

import { Check, Play } from "lucide-react";

import { cn } from "@/lib/cn";
import type { MediaPosition, MessageEntity } from "@/api/templates";
import type { EmojiAsset } from "@/api/emoji";

import { renderSpans, type RenderSpan } from "./entities";

interface ChatPreviewProps {
  text: string;
  entities: MessageEntity[];
  mediaPath?: string | null;
  mediaPosition?: MediaPosition;
  /** Optional sender name shown above the message (group chat style). */
  senderName?: string;
  emojiAssets?: Record<string, EmojiAsset>;
}

export function ChatPreview({
  text,
  entities,
  mediaPath,
  mediaPosition = "top",
  senderName,
  emojiAssets,
}: ChatPreviewProps) {
  const empty = !text.trim() && !mediaPath;
  const now = new Date();
  const time = `${now.getHours().toString().padStart(2, "0")}:${now
    .getMinutes()
    .toString()
    .padStart(2, "0")}`;

  return (
    <div className="relative rounded-2xl overflow-hidden border border-white/[0.06]">
      {/* Wallpaper */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(120% 80% at 0% 0%, rgba(255,107,53,0.10) 0%, rgba(0,0,0,0) 60%), radial-gradient(120% 80% at 100% 100%, rgba(120,80,200,0.10) 0%, rgba(0,0,0,0) 60%), #0b0d10",
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.7) 1px, transparent 0)",
          backgroundSize: "18px 18px",
        }}
      />

      <div className="relative px-4 py-4 min-h-[140px] flex flex-col items-end justify-end gap-1">
        {/* Date separator */}
        <div className="self-center mb-1 px-2.5 py-0.5 rounded-full bg-black/35 backdrop-blur-sm text-[10px] text-white/70 font-medium">
          сегодня
        </div>

        {empty ? (
          <div className="self-center text-xs text-text-dim py-4">
            Начните вводить текст — здесь появится превью
          </div>
        ) : (
          <div className="flex flex-col items-end max-w-[85%]">
            <Bubble
              text={text}
              entities={entities}
              mediaPath={mediaPath}
              mediaPosition={mediaPosition}
              senderName={senderName}
              time={time}
              emojiAssets={emojiAssets}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function Bubble({
  text,
  entities,
  mediaPath,
  mediaPosition = "top",
  senderName,
  time,
  emojiAssets,
}: {
  text: string;
  entities: MessageEntity[];
  mediaPath?: string | null;
  mediaPosition?: MediaPosition;
  senderName?: string;
  time: string;
  emojiAssets?: Record<string, EmojiAsset>;
}) {
  const spans = renderSpans(text || "", entities);
  const groups = groupQuoteSpans(spans);
  const isMediaOnly = !!mediaPath && !text.trim();
  const mediaBottom = mediaPath && mediaPosition === "bottom";

  return (
    <div
      className={cn(
        "relative max-w-full px-4 pt-2.5 pb-2 rounded-2xl rounded-br-md",
        "shadow-[0_8px_24px_-12px_rgba(255,69,0,0.5)]",
        "text-[14.5px] leading-snug",
      )}
      style={{
        // Telegram own-message gradient — orange accent
        background:
          "linear-gradient(135deg, #FF8A4C 0%, #FF6B35 60%, #E2531F 100%)",
        color: "white",
      }}
    >
      {senderName && (
        <div className="text-[12px] font-semibold text-white/90 mb-0.5">
          {senderName}
        </div>
      )}

      {mediaPath && mediaPosition !== "bottom" && <MediaThumb mediaPath={mediaPath} />}

      {text.trim() ? (
        <div
          className={cn(
            "whitespace-pre-wrap break-words",
            mediaPath && mediaPosition !== "bottom" && "mt-1.5",
          )}
        >
          {groups.map((group, i) =>
            group.isQuote ? (
              <span
                key={i}
                className="my-1 block rounded-r-lg border-l-2 border-white/80 bg-black/14 pl-2 pr-1 py-1"
              >
                {group.spans.map((s, j) => (
                  <BubbleSpan key={j} span={s} emojiAssets={emojiAssets} suppressQuote />
                ))}
              </span>
            ) : (
              group.spans.map((s, j) => (
                <BubbleSpan key={`${i}-${j}`} span={s} emojiAssets={emojiAssets} />
              ))
            ),
          )}
          {/* Inline time slot — pushes ticks to end-of-line if it fits */}
          <span className="inline-block w-[64px]" aria-hidden />
        </div>
      ) : null}

      {mediaBottom && <MediaThumb mediaPath={mediaPath} className="mt-1.5" />}

      <div
        className={cn(
          "flex items-center gap-1 select-none",
          isMediaOnly
            ? "absolute right-2.5 bottom-2.5 bg-black/35 backdrop-blur-sm rounded-full px-1.5 py-0.5"
            : "absolute right-3 bottom-2",
        )}
      >
        <span className="text-[10.5px] text-white/85 tabular-nums leading-none">
          {time}
        </span>
        <DoubleTick />
      </div>
    </div>
  );
}

function groupQuoteSpans(spans: RenderSpan[]): Array<{ isQuote: boolean; spans: RenderSpan[] }> {
  const groups: Array<{ isQuote: boolean; spans: RenderSpan[] }> = [];
  for (const span of spans) {
    const isQuote = span.types.some((t) => t.type === "blockquote");
    const prev = groups[groups.length - 1];
    if (prev && prev.isQuote === isQuote) {
      prev.spans.push(span);
    } else {
      groups.push({ isQuote, spans: [span] });
    }
  }
  return groups;
}

function MediaThumb({ mediaPath, className }: { mediaPath: string; className?: string }) {
  const isVideo = /\.(mp4|mov|m4v|webm)$/i.test(mediaPath);
  const src = mediaPath.startsWith("http") || mediaPath.startsWith("/") ? mediaPath : `/api/media/${mediaPath}`;
  return (
    <div
      className={cn(
        "relative -mx-1.5 mb-1 rounded-xl overflow-hidden",
        className,
        "aspect-[4/3] bg-black/30 grid place-items-center",
        "ring-1 ring-white/15",
      )}
    >
      {isVideo ? (
        <video src={src} className="absolute inset-0 size-full object-cover" muted playsInline />
      ) : (
        <img src={src} alt="" className="absolute inset-0 size-full object-cover" loading="lazy" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent" />
      <div className="relative text-white/90 flex flex-col items-center gap-1">
        {isVideo && <Play className="size-7 fill-white drop-shadow" />}
        <div className="text-[10px] uppercase tracking-wider text-white/70 drop-shadow">
          {isVideo ? "видео" : ""}
        </div>
      </div>
    </div>
  );
}

function DoubleTick() {
  // Telegram-style "read" ticks, slightly overlapped.
  return (
    <span className="relative inline-flex items-center w-[14px] h-[10px]">
      <Check className="absolute left-0 size-[10px] text-white/95" strokeWidth={3} />
      <Check className="absolute left-1 size-[10px] text-white/95" strokeWidth={3} />
    </span>
  );
}

function BubbleSpan({
  span,
  emojiAssets,
  suppressQuote = false,
}: {
  span: RenderSpan;
  emojiAssets?: Record<string, EmojiAsset>;
  suppressQuote?: boolean;
}) {
  const { text, types } = span;
  const classes: string[] = [];
  let isCode = false;
  let isPre = false;
  let isSpoiler = false;
  let isLink = false;
  let isQuote = false;
  let emoji: MessageEntity | undefined;

  for (const t of types) {
    if (t.type === "bold") classes.push("font-semibold");
    else if (t.type === "italic") classes.push("italic");
    else if (t.type === "underline") classes.push("underline");
    else if (t.type === "strikethrough") classes.push("line-through");
    else if (t.type === "code") isCode = true;
    else if (t.type === "pre") isPre = true;
    else if (t.type === "spoiler") isSpoiler = true;
    else if (t.type === "blockquote") isQuote = true;
    else if (t.type === "text_link" || t.type === "mention" || t.type === "text_mention")
      isLink = true;
    else if (t.type === "custom_emoji") emoji = t;
  }

  if (isPre) {
    return (
      <code
        className={cn(
          "block my-1 px-2 py-1.5 rounded-lg text-[12.5px] font-mono leading-relaxed whitespace-pre",
          classes.join(" "),
        )}
        style={{ background: "rgba(0,0,0,0.28)", color: "white" }}
      >
        {text}
      </code>
    );
  }
  if (isCode) {
    return (
      <code
        className={cn("rounded px-1 py-0.5 text-[13px] font-mono", classes.join(" "))}
        style={{ background: "rgba(0,0,0,0.28)", color: "white" }}
      >
        {text}
      </code>
    );
  }
  if (isSpoiler) {
    return (
      <span
        className={cn("rounded px-0.5", classes.join(" "))}
        style={{ background: "rgba(0,0,0,0.45)", color: "rgba(255,255,255,0.55)" }}
      >
        {text}
      </span>
    );
  }
  if (isQuote && !suppressQuote && !emoji) {
    return (
      <span
        className={cn(
          "my-1 inline-block w-full rounded-r-md border-l-2 border-white/70 bg-black/15 px-2 py-1",
          classes.join(" "),
        )}
      >
        {text}
      </span>
    );
  }
  if (isLink) {
    return (
      <span
        className={cn("underline decoration-white/70 underline-offset-2", classes.join(" "))}
      >
        {text}
      </span>
    );
  }
  if (emoji) {
    const asset = emoji.custom_emoji_id
      ? emojiAssets?.[String(emoji.custom_emoji_id)]
      : undefined;
    return (
      <span className={cn("relative inline-block", classes.join(" "))}>
        {asset?.image_url ? (
          <img
            src={asset.image_url}
            alt={text}
            className="inline-block size-[1.22em] align-[-0.22em] object-contain"
            loading="lazy"
          />
        ) : (
          <span className="text-[17px] leading-none align-[-2px]">{text}</span>
        )}
        {/* Premium emoji marker — tiny accent dot */}
        <span
          aria-hidden
          className="absolute -top-0.5 -right-0.5 size-1.5 rounded-full bg-white shadow-[0_0_6px_rgba(255,255,255,0.9)]"
        />
      </span>
    );
  }

  return <span className={classes.join(" ")}>{text}</span>;
}
